import axios from 'axios'
import Cookies from 'js-cookie'
import Swal from 'sweetalert2';
import { MODAL_LAYERS } from '@/constants/modalLayers';
import { getCertNameMode } from '@/utils/certNameMode'
import { cognitoLogout } from '@/lib/auth'

const LOG_STORAGE_KEY = 'debug_logs_jsonl';
const LOG_FLAG_KEY = 'app_debug_mode';
const AUTH_LOG_ENDPOINTS = new Set([
  '/api/auth/callback',
  '/api/auth/verify-token',
  '/api/auth/refresh-access-token',
]);


if (typeof window !== 'undefined') {
  const params = new URLSearchParams(window.location.search);
  const debugParam = params.get('debug');

  if (debugParam === '1') {
    localStorage.setItem(LOG_FLAG_KEY, 'true');
    console.log('%c[DEBUG MODE] ON', 'color: green; font-weight: bold');
  } else if (debugParam === '0') {
    localStorage.removeItem(LOG_FLAG_KEY);
    console.log('%c[DEBUG MODE] OFF', 'color: gray; font-weight: bold');
  } else {
    if (localStorage.getItem(LOG_FLAG_KEY) === null) {
      localStorage.setItem(LOG_FLAG_KEY, 'true');
      console.log('%c[DEBUG MODE] ON (default)', 'color: green; font-weight: bold');
    }
  }
}


function isDebugEnabled(): boolean {
  if (typeof window === 'undefined') return false;
  return localStorage.getItem(LOG_FLAG_KEY) === 'true';
}


function sendToApi(data: any) {
  fetch('/api/log', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
    keepalive: true
  }).catch(() => { /* Silent fail */ });
}

function saveToStorage(data: any) {
  try {
    const str = JSON.stringify(data) + '\n';
    const old = localStorage.getItem(LOG_STORAGE_KEY) || '';
    if (old.length < 4000000) {
      localStorage.setItem(LOG_STORAGE_KEY, old + str);
    }
  } catch (e) { }
}

function getLogUrl(url?: string): string | undefined {
  if (!url) return url;

  const urlWithoutQuery = url.split('?')[0];
  let path = urlWithoutQuery;

  if (urlWithoutQuery.startsWith('http')) {
    try {
      path = new URL(urlWithoutQuery).pathname;
    } catch {
      return url;
    }
  }

  if (AUTH_LOG_ENDPOINTS.has(path)) return path;
  return url;
}

const logger = {
  info: (message: string, meta: any) => {
    if (!isDebugEnabled()) return;

    const logData = {
      timestamp: new Date().toISOString(),
      level: 'info',
      message,
      ...meta
    };
    console.log(`%c[INFO] ${message}`, 'color: blue;', meta);
    sendToApi(logData);
    saveToStorage(logData);
  },

  error: (message: string, meta: any) => {
    if (!isDebugEnabled()) return;

    const logData = {
      timestamp: new Date().toISOString(),
      level: 'error',
      message,
      ...meta
    };
    sendToApi(logData);
    saveToStorage(logData);
  }
};


function exportLogsAsJsonl() {
  const logs = localStorage.getItem(LOG_STORAGE_KEY)
  if (!logs) { alert('No logs found'); return; }
  const blob = new Blob([logs], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `log_fe_${Date.now()}.jsonl`;
  a.click();
}

if (typeof window !== 'undefined') {
  (window as any).exportDebugLogs = exportLogsAsJsonl;
  (window as any).clearDebugLogs = () => localStorage.removeItem(LOG_STORAGE_KEY);
}

function generateRequestId(): string {
  const cryptoAny: any = (globalThis as any).crypto
  if (cryptoAny?.randomUUID) return cryptoAny.randomUUID()
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`
}

const CERT_NAME_MODE_ENDPOINTS = new Set([
  '/cloud-app/notification-processing/get',
  '/cloud-app/notification-history/get',
  '/cloud-app/registration-information-list/get',
  '/report/available-items',
])

function usesCertNameMode(url: string | undefined, method: string | undefined): boolean {
  const path = url?.split('?')[0] ?? ''
  if (path.startsWith('/api/admin/card')) return method?.toLowerCase() === 'get'
  return CERT_NAME_MODE_ENDPOINTS.has(path)
}

const axiosInstance = axios.create({
  baseURL: process.env.NEXT_PUBLIC_API_BASE_URL || '',
  timeout: 50000,
  validateStatus: (status) => status >= 200 && status < 300,
})

let isRefreshing = false;
let failedQueue: Array<{ resolve: (value?: unknown) => void; reject: (reason?: any) => void }> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Request Interceptor
axiosInstance.interceptors.request.use(
  (config) => {
    const token = Cookies.get('access_token')
    config.headers = config.headers || {}
    if (token) config.headers.Authorization = `Bearer ${token}`
    if (!config.headers['X-Request-Id']) config.headers['X-Request-Id'] = generateRequestId()

    if (usesCertNameMode(config.url, config.method)) {
      const params = config.params as Record<string, unknown> | undefined
      if (params?.name_mode === undefined) {
        config.params = { ...params, name_mode: getCertNameMode() }
      }
    }

    // Log request
    if (isDebugEnabled()) {
      logger.info('API Request', {
        type: 'request',
        request_id: config.headers['X-Request-Id'],
        method: config.method,
        url: getLogUrl(config.url),
      });
    }
    return config
  },
  (error) => Promise.reject(error),
)

// Response Interceptor
axiosInstance.interceptors.response.use(
  (response) => {
    // Log success
    if (isDebugEnabled()) {
      logger.info('API Success', {
        type: 'success',
        request_id: response.config.headers?.['X-Request-Id'],
        status: response.status,
        url: getLogUrl(response.config.url),
      });
    }
    return response
  },
  (error) => {
    // Log error
    if (isDebugEnabled()) {
      logger.error('API Error', {
        type: 'fail',
        request_id: error.config?.headers?.['X-Request-Id'],
        status: error.response?.status,
        url: getLogUrl(error.config?.url),
        message: error.message,
      });
    }

    const status = error.response?.status
    const apiError = error.response?.data?.error

    // 401: セッション切れ
    if (status === 401 && typeof window !== 'undefined') {
      const originalRequest = error.config;
      
      // If the error comes from the refresh token endpoint, log out
      if (originalRequest.url?.includes('/api/auth/refresh-access-token')) {
        Cookies.remove('access_token');
        Cookies.remove('refresh_token');
        Cookies.remove('user_role');
        Cookies.remove('user_name');
        localStorage.removeItem('notification_filter_settings');
        cognitoLogout();
        return Promise.reject(error);
      }

      const refreshToken = Cookies.get('refresh_token');

      if (!originalRequest._retry && refreshToken) {
        if (isRefreshing) {
          return new Promise(function(resolve, reject) {
            failedQueue.push({ resolve, reject });
          }).then(token => {
            originalRequest.headers.Authorization = 'Bearer ' + token;
            return axiosInstance(originalRequest);
          }).catch(err => {
            return Promise.reject(err);
          });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        return new Promise(function (resolve, reject) {
          axios.get(
            (process.env.NEXT_PUBLIC_API_BASE_URL || '') +
              `/api/auth/refresh-access-token?refresh_token=${encodeURIComponent(refreshToken)}`
          ).then((res) => {
            const newAccessToken = res.data?.access_token || res.data?.data?.access_token;
            const newRefreshToken = res.data?.refresh_token || res.data?.data?.refresh_token || refreshToken;
            
            const secure = window.location.protocol === 'https:';
            Cookies.set('access_token', newAccessToken, { path: '/', secure });
            Cookies.set('refresh_token', newRefreshToken, { path: '/', secure });

            axiosInstance.defaults.headers.common['Authorization'] = 'Bearer ' + newAccessToken;
            originalRequest.headers.Authorization = 'Bearer ' + newAccessToken;

            processQueue(null, newAccessToken);
            resolve(axiosInstance(originalRequest));
          })
          .catch((err) => {
            processQueue(err, null);
            Cookies.remove('access_token');
            Cookies.remove('refresh_token');
            Cookies.remove('user_role');
            Cookies.remove('user_name');
            localStorage.removeItem('notification_filter_settings');
            cognitoLogout();
            reject(err);
          })
          .finally(() => {
            isRefreshing = false;
          });
        });
      }

      Cookies.remove('access_token');
      Cookies.remove('refresh_token');
      Cookies.remove('user_role');
      Cookies.remove('user_name');
      localStorage.removeItem('notification_filter_settings');
      cognitoLogout();
      return Promise.reject(error)
    }

    // BEの構造化エラー: 日本語メッセージをそのまま表示
    if (apiError?.message) {
      Swal.fire({
        icon: 'error',
        title: 'エラーが発生しました',
        text: apiError.message,
        confirmButtonText: 'OK',
        didOpen: () => {
          const swalContainer = document.querySelector('.swal2-container') as HTMLElement
          if (swalContainer) swalContainer.style.zIndex = String(MODAL_LAYERS.apiError)
        },
      })
      return Promise.reject(error)
    }

    // 構造化エラーなし（ネットワークエラー等）
    Swal.fire({
      icon: 'error',
      title: 'エラーが発生しました',
      text: '不明なエラーが発生しました。画面を再読み込みするか、管理者にお問い合わせください。',
      confirmButtonText: 'OK',
      didOpen: () => {
        const swalContainer = document.querySelector('.swal2-container') as HTMLElement
        if (swalContainer) swalContainer.style.zIndex = String(MODAL_LAYERS.apiError)
      },
    })
    return Promise.reject(error)
  },
)

export default axiosInstance
